import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // На продакшене используем относительный путь
  : 'http://127.0.0.1:8000/api'; // Локально используем прямой URL

// Важно: refresh-токен у нас одноразовый (сервер его blacklist'ит и выдаёт новый).
// Поэтому НЕЛЬЗЯ параллельно делать несколько refresh-запросов: второй запрос с тем же refresh упадёт,
// и пользователь будет "вылетать" из системы. Делаем single-flight refresh.
let refreshInFlight = null;

const isApiDebug = () => {
  try { return localStorage.getItem('aot_debug_api') === '1'; } catch (_) { return false; }
};

const getStoredTokens = () => {
  try {
    const raw = localStorage.getItem('tokens');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const setStoredTokens = (tokens) => {
  try {
    localStorage.setItem('tokens', JSON.stringify(tokens || {}));
  } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent('aot_tokens_updated', { detail: tokens || {} }));
  } catch (_) {}
};

const clearStoredAuth = () => {
  try { localStorage.removeItem('tokens'); } catch (_) {}
  try { localStorage.removeItem('user'); } catch (_) {}
};

const rememberLastRoute = () => {
  try {
    const hash = window.location.hash || '';
    const path = hash.startsWith('#') ? hash.slice(1) : hash;
    if (path && !path.startsWith('/login') && !path.startsWith('/register')) {
      sessionStorage.setItem('aot_last_route', path);
    }
  } catch (_) {}
};

const refreshTokensSingleFlight = async (refreshToken) => {
  const token = String(refreshToken || '').trim();
  if (!token) {
    throw new Error('refresh_token_missing');
  }

  // Если уже есть активный refresh, просто ждём его (важно для одноразового refresh токена)
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = axios
    .post(`${API_BASE_URL}/auth/refresh/`, { refresh: token })
    .then((response) => {
      if (isApiDebug()) console.log('Token refresh successful');
      const prev = getStoredTokens() || {};
      const newTokens = { ...prev, ...response.data };
      setStoredTokens(newTokens);
      return newTokens;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

// Внимание: JWT храним в localStorage и передаём через Authorization,
// поэтому cookies не используем и withCredentials выключаем, чтобы избежать CORS-проблем
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен в заголовки запросов
api.interceptors.request.use(
  (config) => {
    const tokens = getStoredTokens();
    if (isApiDebug()) {
      console.log('Request interceptor - URL:', config.url, 'Method:', config.method);
      console.log('Tokens in localStorage:', tokens ? 'present' : 'missing');
    }
    
    if (tokens) {
      const { access } = tokens;
      if (isApiDebug()) console.log('Adding Authorization header with token:', access ? access.substring(0, 20) + '...' : 'missing');
      config.headers.Authorization = `Bearer ${access}`;
    } else {
      if (isApiDebug()) console.log('No tokens found, request will be sent without Authorization header');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isApiDebug()) console.log('API Error intercepted:', error.response?.status, error.config?.url);
    
    if (error.response?.status === 401) {
      // Не пытаться "обновлять токен через обновление токена"
      if (String(error.config?.url || '').includes('/auth/refresh/')) {
        clearStoredAuth();
        rememberLastRoute();
        window.location.href = '/#/login';
        return Promise.reject(error);
      }

      // Защита от бесконечных повторов
      if (error.config && error.config._retry) {
        return Promise.reject(error);
      }
      if (isApiDebug()) console.log('401 error detected, attempting token refresh');
      // Попытка обновить токен (single-flight)
      const tokens = getStoredTokens();
      if (isApiDebug()) console.log('Current tokens in localStorage:', tokens ? 'present' : 'missing');
      
      if (tokens) {
        try {
          const { refresh } = tokens;
          if (isApiDebug()) console.log('Attempting refresh with token:', refresh ? 'present' : 'missing');
          
          const newTokens = await refreshTokensSingleFlight(refresh);
          
          // Повторяем оригинальный запрос
          if (error.config) error.config._retry = true;
          error.config.headers.Authorization = `Bearer ${newTokens.access}`;
          if (isApiDebug()) console.log('Retrying original request with new token');
          return api.request(error.config);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          // Если refresh упал, но за это время токены уже успели обновиться (например, другой вкладкой/процессом),
          // пробуем один раз повторить запрос с актуальным access.
          const current = getStoredTokens();
          const hasNewAccess = !!current?.access && current?.access !== tokens?.access;
          const hasNewRefresh = !!current?.refresh && current?.refresh !== tokens?.refresh;

          if ((hasNewAccess || hasNewRefresh) && error.config && !error.config._retry2) {
            try {
              error.config._retry2 = true;
              error.config.headers.Authorization = `Bearer ${current.access}`;
              return api.request(error.config);
            } catch (_) {
              // упали второй раз — чистим и уводим на логин
            }
          }

          clearStoredAuth();
          rememberLastRoute();
          window.location.href = '/#/login';
        }
      } else {
        console.log('No tokens found, redirecting to login');
        rememberLastRoute();
        window.location.href = '/#/login';
      }
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  ensureAccessToken: async () => {
    const tokens = getStoredTokens();
    if (!tokens) return '';
    const access = tokens.access || '';
    const refresh = tokens.refresh || '';
    if (!refresh) return access;
    try {
      const newTokens = await refreshTokensSingleFlight(refresh);
      return newTokens?.access || access;
    } catch (_) {
      return access;
    }
  },
  getAccessToken: () => {
    const tokens = getStoredTokens();
    return tokens?.access || '';
  },
  getDebtorInnFillStreamUrl: (token) => {
    const t = token || apiService.getAccessToken();
    return `${API_BASE_URL}/debtors/fill-by-inn-stream/?token=${encodeURIComponent(t || '')}`;
  },
  getManagerInnFillStreamUrl: (token) => {
    const t = token || apiService.getAccessToken();
    return `${API_BASE_URL}/managers/fill-by-inn-stream/?token=${encodeURIComponent(t || '')}`;
  },
  // Authentication
  login: (email, password) => api.post('/auth/login/', { email, password }),
  register: (userData) => api.post('/auth/register/', userData),
  requestEmailVerification: (email) => api.post('/auth/email/request/', { email }),
  confirmEmailVerification: (email, code) => api.post('/auth/email/confirm/', { email, code }),
  activateEmailVerification: (token) => api.get(`/auth/email/activate/?token=${encodeURIComponent(token)}`),
  getPasswordResetCaptcha: () => api.get('/auth/password/captcha/'),
  requestPasswordReset: (payload) => api.post('/auth/password/request/', payload),
  confirmPasswordReset: (email, code) => api.post('/auth/password/confirm/', { email, code }),
  activatePasswordReset: (token) => api.get(`/auth/password/activate/?token=${encodeURIComponent(token)}`),
  resetPassword: (payload) => api.post('/auth/password/reset/', payload),
  testEmailSettings: (toEmail) => api.post('/auth/email/test/', { to_email: toEmail }),
  testTelegramSettings: () => api.post('/auth/telegram/test/'),
  testTelegramProxy: () => api.post('/auth/telegram/proxy/test/'),
  getEmailSettings: () => api.get('/auth/email/settings/'),
  saveEmailSettings: (data) => api.put('/auth/email/settings/', data),
  getUiSettings: () => api.get('/auth/ui/settings/'),
  saveUiSettings: (data) => api.put('/auth/ui/settings/', data),
  lookupBankByBik: (bik) => api.post('/accounts/bank-lookup/', { mode: 'bik', bik }),
  searchBanksByName: (query) => api.post('/accounts/bank-lookup/', { mode: 'name', query }),
  logout: (refreshToken) => api.post('/auth/logout/', { refresh: refreshToken }),
  refreshToken: (refreshToken) => api.post('/auth/refresh/', { refresh: refreshToken }),
  // Обновление токенов без гонок (single-flight)
  refreshTokensSingleFlight: (refreshToken) => refreshTokensSingleFlight(refreshToken),
  getUserProfile: () => api.get('/auth/profile/'),
  updateUserProfile: (userData) => api.put('/auth/profile/update/', userData),
  getFedresursBinding: () => api.get('/auth/fedresurs/binding/'),
  saveFedresursBinding: (data) => api.put('/auth/fedresurs/binding/', data),
  
  // Users
  getUsers: () => api.get('/users/users/'),
  getUser: (id) => api.get(`/users/users/${id}/`),
  createUser: (data) => api.post('/users/users/', data),
  updateUser: (id, data) => api.put(`/users/users/${id}/`, data),
  deleteUser: (id) => api.delete(`/users/users/${id}/`),

  // Debtors
  getDebtors: (params = '') => api.get(`/debtors/${params}`),
  getDebtor: (id) => api.get(`/debtors/${id}/`),
  createDebtor: (data) => api.post('/debtors/', data),
  updateDebtor: (id, data) => api.put(`/debtors/${id}/`, data),
  deleteDebtor: (id) => api.delete(`/debtors/${id}/`),

  // Organizers
  getOrganizers: (params = '') => api.get(`/organizers/${params}`),
  getOrganizer: (id) => api.get(`/organizers/${id}/`),
  createOrganizer: (data) => api.post('/organizers/', data),
  updateOrganizer: (id, data) => api.put(`/organizers/${id}/`, data),
  deleteOrganizer: (id) => api.delete(`/organizers/${id}/`),
  fillOrganizerByInn: (payload) => api.post('/organizers/fill-by-inn/', payload),
  getOrganizerInnFillStatus: (taskId) => {
    const qs = taskId ? `?task_id=${encodeURIComponent(taskId)}` : '';
    return api.get(`/organizers/fill-by-inn-status/${qs}`);
  },
  getOrganizerInnFillResult: (taskId) => api.get(`/organizers/fill-by-inn-result/?task_id=${encodeURIComponent(taskId || '')}`),
  getOrganizerInnFillStreamUrl: (token) => {
    const t = token || apiService.getAccessToken();
    return `${API_BASE_URL}/organizers/fill-by-inn-stream/?token=${encodeURIComponent(t || '')}`;
  },

  // Managers (Арбитражные управляющие)
  getManagers: (params = '') => api.get(`/managers/${params}`),
  getManager: (id) => api.get(`/managers/${id}/`),
  createManager: (data) => api.post('/managers/', data),
  updateManager: (id, data) => api.put(`/managers/${id}/`, data),
  deleteManager: (id) => api.delete(`/managers/${id}/`),

  // Журнал активности
  getActivityLogs: (params = '') => api.get(`/activities/activity-logs/${params}`),
  truncateActivityLogsBefore: (dateTo) => api.post('/activities/activity-logs/truncate-before/', { date_to: dateTo }),
  getUserActivityLogs: (userId, query = '') => {
    const raw = String(query || '').trim();
    const suffix = raw ? (raw.startsWith('?') ? `&${raw.slice(1)}` : `&${raw}`) : '';
    return api.get(`/activities/activity-logs/?user_id=${encodeURIComponent(String(userId || ''))}${suffix}`);
  },

  // SRO
  getSros: (params = '') => api.get(`/sros/${params}`),
  getSro: (id) => api.get(`/sros/${id}/`),
  createSro: (data) => api.post('/sros/', data),
  updateSro: (id, data) => api.put(`/sros/${id}/`, data),
  deleteSro: (id) => api.delete(`/sros/${id}/`),
  exportSros: () => api.get('/sros/export/', { responseType: 'blob' }),
  importSros: (data) => api.post('/sros/import_data/', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  startSroImport: () => api.post('/sros/import-jobs/start/'),
  startSroShortNameUpdate: () => api.post('/sros/import-jobs/start_short_name_update/'),
  getSroImportJob: (id) => api.get(`/sros/import-jobs/${id}/`),
  listSroImportJobs: (params = '') => api.get(`/sros/import-jobs/${params}`),
  fillDebtorByInn: (payload) => api.post('/debtors/fill-by-inn/', payload),
  getDebtorInnFillStatus: (taskId) => {
    const qs = taskId ? `?task_id=${encodeURIComponent(taskId)}` : '';
    return api.get(`/debtors/fill-by-inn-status/${qs}`);
  },
  getDebtorInnFillResult: (taskId) => api.get(`/debtors/fill-by-inn-result/?task_id=${encodeURIComponent(taskId || '')}`),
  fillManagerByInn: (payload) => api.post('/managers/fill-by-inn/', payload),
  getManagerInnFillStatus: (taskId) => {
    const qs = taskId ? `?task_id=${encodeURIComponent(taskId)}` : '';
    return api.get(`/managers/fill-by-inn-status/${qs}`);
  },
  getManagerInnFillResult: (taskId) => api.get(`/managers/fill-by-inn-result/?task_id=${encodeURIComponent(taskId || '')}`),

  // Courts
  getCourts: (params = '') => api.get(`/courts/${params}`),
  getCourt: (id) => api.get(`/courts/${id}/`),
  createCourt: (data) => api.post('/courts/', data),
  updateCourt: (id, data) => api.put(`/courts/${id}/`, data),
  deleteCourt: (id) => api.delete(`/courts/${id}/`),
  startCourtImport: () => api.post('/courts/import-jobs/start/'),
  getCourtImportJob: (id) => api.get(`/courts/import-jobs/${id}/`),
  listCourtImportJobs: (params = '') => api.get(`/courts/import-jobs/${params}`),

  // Classifiers
  getClassifiers: (params = '') => api.get(`/classifiers/${params}`),
  getClassifier: (id) => api.get(`/classifiers/${id}/`),
  startClassifierImport: () => api.post('/classifiers/import-jobs/start/'),
  getClassifierImportJob: (id) => api.get(`/classifiers/import-jobs/${id}/`),
  listClassifierImportJobs: (params = '') => api.get(`/classifiers/import-jobs/${params}`),

  // Reminders
  getReminders: (params = '') => api.get(`/reminders/reminders/${params}`),
  getReminder: (id) => api.get(`/reminders/reminders/${id}/`),
  createReminder: (data) => api.post('/reminders/reminders/', data),
  updateReminder: (id, data) => api.put(`/reminders/reminders/${id}/`, data),
  deleteReminder: (id) => api.delete(`/reminders/reminders/${id}/`),
  getRemindersSummary: (params = '') => api.get(`/reminders/reminders/summary/${params}`),
  listReminderNotifications: (params = '') => api.get(`/reminders/notifications/${params}`),
  markReminderNotificationRead: (id) => api.post(`/reminders/notifications/${id}/mark_read/`),

  // Техническая поддержка
  getSupportRequests: (params = '') => api.get(`/support/requests/${params}`),
  getSupportRequest: (id) => api.get(`/support/requests/${id}/`),
  createSupportRequest: (data) => api.post('/support/requests/', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateSupportRequest: (id, data) => api.patch(`/support/requests/${id}/`, data),
  addSupportMessage: (id, data) => api.post(`/support/requests/${id}/messages/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  markSupportRequestRead: (id) => api.post(`/support/requests/${id}/mark_read/`),
  getSupportSummary: () => api.get('/support/requests/summary/'),
  downloadSupportAttachment: (id) => api.get(`/support/attachments/${id}/`, { responseType: 'blob' }),

  // Bank Accounts
  getBankAccounts: (query = '') => api.get(`/accounts/${query}`),
  getBankAccount: (id) => api.get(`/accounts/${id}/`),
  createBankAccount: (data) => api.post('/accounts/', data),
  updateBankAccount: (id, data) => api.put(`/accounts/${id}/`, data),
  deleteBankAccount: (id) => api.delete(`/accounts/${id}/`),

  // Auctions
  getAuctions: (params = '') => api.get(`/auctions/${params}`),
  getAuction: (id) => api.get(`/auctions/${id}/`),
  createAuction: (data) => api.post('/auctions/', data),
  updateAuction: (id, data) => api.put(`/auctions/${id}/`, data),
  patchAuction: (id, data) => api.patch(`/auctions/${id}/`, data),
  deleteAuction: (id) => api.delete(`/auctions/${id}/`),
  createRepeatAuction: (id) => api.post(`/auctions/${id}/create_repeat/`),
  getBankAccountsForAuctions: () => api.get('/auctions/bank_accounts/'),
  startFedresursExport: (auctionId, data = {}) => api.post(`/auctions/${auctionId}/fedresurs_export/`, data),
  listFedresursExportsByAuction: (auctionId) => api.get(`/auctions/${auctionId}/fedresurs_exports/`),

  // Lots
  getLots: (params = '') => api.get(`/lots/${params}`),
  getLot: (id) => api.get(`/lots/${id}/`),
  createLot: (data) => api.post('/lots/', data),
  updateLot: (id, data) => api.put(`/lots/${id}/`, data),
  patchLot: (id, data) => api.patch(`/lots/${id}/`, data),
  deleteLot: (id) => api.delete(`/lots/${id}/`),

  // Creditors
  getCreditors: (params = '') => api.get(`/creditors/${params}`),
  getCreditor: (id) => api.get(`/creditors/${id}/`),
  createCreditor: (data) => api.post('/creditors/', data),
  updateCreditor: (id, data) => api.put(`/creditors/${id}/`, data),
  deleteCreditor: (id) => api.delete(`/creditors/${id}/`),

  // ETPs
  getEtps: (params = '') => api.get(`/etps/${params}`),
  getEtp: (id) => api.get(`/etps/${id}/`),
  createEtp: (data) => api.post('/etps/', data),
  updateEtp: (id, data) => api.put(`/etps/${id}/`, data),
  deleteEtp: (id) => api.delete(`/etps/${id}/`),
  exportEtps: () => api.get('/etps/export/', { responseType: 'blob' }),
  importEtps: (data) => api.post('/etps/import_data/', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  startEtpImport: () => api.post('/etps/import-jobs/start/'),
  getEtpImportJob: (id) => api.get(`/etps/import-jobs/${id}/`),
  listEtpImportJobs: (params = '') => api.get(`/etps/import-jobs/${params}`),
  startEtpOperatorUpdate: () => api.post('/etps/import-jobs/start_operator_update/'),
  getEtpProfileBinding: () => api.get('/etps/profile-binding/'),
  saveEtpProfileBinding: (data) => api.put('/etps/profile-binding/', data),
  startEtpDraftExport: (payload) => api.post('/etps/exports/', payload),
  getEtpDraftExportJob: (jobId) => api.get(`/etps/exports/${jobId}/`),
  listEtpDraftExportsByAuction: (auctionId) => api.get(`/etps/auctions/${auctionId}/exports/`),
  
  // Auction Lots
  getAuctionLots: (auctionId) => api.get(`/auctions/${auctionId}/lots/`),
  addLotToAuction: (auctionId, lotData) => api.post(`/auctions/${auctionId}/add_lot/`, lotData),
  detachLotFromAuction: (lotId) => api.post(`/lots/${lotId}/detach/`),

  // Templates
  getTemplates: () => api.get('/templates/'),
  getTemplate: (id) => api.get(`/templates/${id}/`),
  createTemplate: (data) => api.post('/templates/', data),
  updateTemplate: (id, data) => api.put(`/templates/${id}/`, data),
  deleteTemplate: (id) => api.delete(`/templates/${id}/`),

  // Documents
  getDocuments: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/documents/documents/${queryString ? '?' + queryString : ''}`);
  },
  getDocument: (id) => api.get(`/documents/documents/${id}/`),
  createDocument: (data) => api.post('/documents/documents/', data),
  updateDocument: (id, data) => api.put(`/documents/documents/${id}/`, data),
  patchDocument: (id, data) => api.patch(`/documents/documents/${id}/`, data),
  deleteDocument: (id) => api.delete(`/documents/documents/${id}/`),
  
  // Template Types
  getTemplateTypes: () => api.get('/documents/template-types/'),
  getTemplateType: (id) => api.get(`/documents/template-types/${id}/`),
  createTemplateType: (data) => api.post('/documents/template-types/', data),
  updateTemplateType: (id, data) => api.put(`/documents/template-types/${id}/`, data),
  deleteTemplateType: (id) => api.delete(`/documents/template-types/${id}/`),

  // Document Templates
  getDocumentTemplates: (type = null) => {
    const url = type ? `/documents/templates/?type=${type}` : '/documents/templates/';
    return api.get(url);
  },
  getDocumentTemplate: (id) => api.get(`/documents/templates/${id}/`),
  createDocumentTemplate: (data) => api.post('/documents/templates/', data),
  updateDocumentTemplate: (id, data) => api.put(`/documents/templates/${id}/`, data),
  deleteDocumentTemplate: (id) => api.delete(`/documents/templates/${id}/`),
  duplicateDocumentTemplate: (id) => api.post(`/documents/templates/${id}/duplicate/`),
  createDocumentFromTemplate: (templateId, data) => api.post(`/documents/templates/${templateId}/create_from_template/`, data),
  
  // Legacy document methods (keep for compatibility)
  generateDocument: (data) => api.post('/documents/generate/', data),
  downloadDocument: (id) => api.get(`/documents/${id}/download/`, { responseType: 'blob' }),
  exportDocumentToWord: (id) => api.get(`/documents/documents/${id}/export_word/`, { responseType: 'blob' }),
  exportDocumentToWordWithVariables: (id, variablesData) => api.post(`/documents/documents/${id}/export_word_with_variables/`, { variables_data: variablesData }, { responseType: 'blob' }),
  getDocumentPlaceholders: () => api.get('/documents/placeholders/'),

  // Presets для условий IF в редакторе шаблонов
  getConditionPresets: () => api.get('/documents/condition-presets/'),
  createConditionPreset: (data) => api.post('/documents/condition-presets/', data),
  updateConditionPreset: (id, data) => api.put(`/documents/condition-presets/${id}/`, data),
  deleteConditionPreset: (id) => api.delete(`/documents/condition-presets/${id}/`),

  // Event Logs
  getEventLogs: () => api.get('/logs/'),
  getEventLog: (id) => api.get(`/logs/${id}/`),
};

export default api;
